import { Color, DynamicDrawUsage, InstancedBufferAttribute, InstancedBufferGeometry, Mesh, type BufferGeometry, type ShaderMaterial } from "three";

const INSTANCE_ATTRIBUTES: ReadonlySet<string> = new Set(["iOffset", "iRot", "iScale", "iColor", "iParams"]);

/**
 * Um lote de efeitos de habilidade (Fase 12, Gate B): uma forma, até `capacity` instâncias, um draw
 * call. Antes cada peça e cada anel era uma malha com material próprio, e quatro ultimates com 40
 * assombrações passavam dos 250 draw calls da cena. A cada quadro o dono chama `begin`, `push` por
 * instância viva e `end`; as instâncias são reescritas do zero (são poucas), sem buracos.
 */
export class SkillBatch {
  readonly mesh: Mesh;
  readonly capacity: number;
  private readonly geometry = new InstancedBufferGeometry();
  private readonly offset: InstancedBufferAttribute;
  private readonly rot: InstancedBufferAttribute;
  private readonly scale: InstancedBufferAttribute;
  private readonly color: InstancedBufferAttribute;
  private readonly params: InstancedBufferAttribute;
  private n = 0;

  constructor(shape: BufferGeometry, material: ShaderMaterial, capacity: number, renderOrder = 6) {
    this.capacity = capacity;
    // A forma é dividida (os mesmos buffers na GPU), só as instâncias são deste lote.
    for (const [name, attribute] of Object.entries(shape.attributes)) this.geometry.setAttribute(name, attribute);
    if (shape.index) this.geometry.setIndex(shape.index);
    const attribute = (size: number) => new InstancedBufferAttribute(new Float32Array(capacity * size), size).setUsage(DynamicDrawUsage);
    this.offset = attribute(3);
    this.rot = attribute(4);
    this.scale = attribute(4);
    this.color = attribute(4);
    this.params = attribute(4);
    this.geometry.setAttribute("iOffset", this.offset);
    this.geometry.setAttribute("iRot", this.rot);
    this.geometry.setAttribute("iScale", this.scale);
    this.geometry.setAttribute("iColor", this.color);
    this.geometry.setAttribute("iParams", this.params);
    this.geometry.instanceCount = 0;
    this.mesh = new Mesh(this.geometry, material);
    this.mesh.renderOrder = renderOrder;
    // As instâncias se espalham pela ilha: sem esfera de corte (como os estilhaços).
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
  }

  /** Instâncias escritas neste quadro. */
  get count(): number {
    return this.n;
  }

  begin(): void {
    this.n = 0;
  }

  /**
   * Uma instância: posição, giro (quatérnio), escala por eixo, cor linear, opacidade e os quatro
   * parâmetros do efeito. Falso com o lote cheio.
   */
  push(x: number, y: number, z: number, qx: number, qy: number, qz: number, qw: number, sx: number, sy: number, sz: number, r: number, g: number, b: number, a: number, p0: number, p1: number, p2: number, p3: number): boolean {
    if (this.n >= this.capacity) return false;
    const i = this.n++;
    this.offset.setXYZ(i, x, y, z);
    this.rot.setXYZW(i, qx, qy, qz, qw);
    // Escala − 1 com o 1 em w: o padrão de uma malha solta (0, 0, 0, 1) vira a escala 1.
    this.scale.setXYZW(i, sx - 1, sy - 1, sz - 1, 1);
    this.color.setXYZW(i, r, g, b, a);
    this.params.setXYZW(i, p0, p1, p2, p3);
    return true;
  }

  end(): void {
    this.geometry.instanceCount = this.n;
    this.mesh.visible = this.n > 0;
    if (this.n === 0) return;
    for (const [attribute, size] of [[this.offset, 3], [this.rot, 4], [this.scale, 4], [this.color, 4], [this.params, 4]] as const) {
      attribute.clearUpdateRanges();
      attribute.addUpdateRange(0, this.n * size);
      attribute.needsUpdate = true;
    }
  }

  dispose(): void {
    this.mesh.removeFromParent();
    // Só os buffers das instâncias saem da GPU: a forma (dividida) sai da geometria antes do descarte,
    // que soltaria os buffers dela também. O material é de quem o passou.
    for (const name of Object.keys(this.geometry.attributes)) if (!INSTANCE_ATTRIBUTES.has(name)) this.geometry.deleteAttribute(name);
    this.geometry.setIndex(null);
    this.geometry.dispose();
  }
}

/** Uma instância lida de volta de um lote (testes e sondas): cor em hex sRGB, escala já com o 1. */
export interface BatchInstance {
  offset: [number, number, number];
  scale: [number, number, number];
  /** O giro (quatérnio x, y, z, w). */
  rot: [number, number, number, number];
  hex: string;
  alpha: number;
  params: [number, number, number, number];
}

/** As instâncias escritas no último `end` de um lote, a partir da malha dele. */
export function readBatch(mesh: Mesh): BatchInstance[] {
  const geometry = mesh.geometry as InstancedBufferGeometry;
  const get = (name: string) => geometry.getAttribute(name) as InstancedBufferAttribute;
  const [o, r, s, c, p] = [get("iOffset"), get("iRot"), get("iScale"), get("iColor"), get("iParams")];
  return Array.from({ length: geometry.instanceCount }, (_, i) => ({
    offset: [o.getX(i), o.getY(i), o.getZ(i)],
    scale: [s.getX(i) + 1, s.getY(i) + 1, s.getZ(i) + 1],
    rot: [r.getX(i), r.getY(i), r.getZ(i), r.getW(i)],
    hex: new Color(c.getX(i), c.getY(i), c.getZ(i)).getHexString(),
    alpha: c.getW(i),
    params: [p.getX(i), p.getY(i), p.getZ(i), p.getW(i)],
  }));
}
